import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../../utils/axiosInstance";
import {
  GasPump,
  CheckCircle,
  Clock,
  FunnelSimple,
  ArrowsCounterClockwise,
  PencilSimple,
  Trash,
  X,
} from "@phosphor-icons/react";
import Pagination from "../shared/Pagination";
import PaliwoReconcileView from "./PaliwoReconcileView";
import { payoutDateFromDraft } from "../../utils/payoutDate";

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

const fmtPLN = (v) =>
  Number(v || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function TankowaniaPracownikowTab() {
  const [drafts, setDrafts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | pending | applied
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState(null); // draft being edited
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("list"); // "list" | "reconcile"

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterEmployee ? { employee_id: filterEmployee } : {};
      const { data } = await axios.get(`${API}/employee-paliwo`, { params });
      setDrafts(data || []);
    } catch (err) {
      console.error("Error fetching paliwo drafts:", err);
    } finally {
      setLoading(false);
    }
  }, [filterEmployee]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employees`);
      setEmployees(data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  // Auto-refresh every 30s to pick up new entries
  useEffect(() => {
    const t = setInterval(fetchDrafts, 30000);
    return () => clearInterval(t);
  }, [fetchDrafts]);

  const filtered = useMemo(() => {
    return drafts.filter((d) => {
      // status filter
      if (filterStatus === "pending" && d.applied) return false;
      if (filterStatus === "applied" && !d.applied) return false;
      // date range filter (d.data is YYYY-MM-DD)
      if (filterDateFrom && d.data && d.data < filterDateFrom) return false;
      if (filterDateTo && d.data && d.data > filterDateTo) return false;
      return true;
    });
  }, [drafts, filterStatus, filterDateFrom, filterDateTo]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterEmployee, filterDateFrom, filterDateTo, filterStatus, pageSize]);

  // Paginated slice
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const totals = useMemo(() => {
    const sum = filtered.reduce((s, d) => s + Number(d.kwota || 0), 0);
    const applied = filtered.filter((d) => d.applied).length;
    return { sum, count: filtered.length, applied, pending: filtered.length - applied };
  }, [filtered]);

  const onDelete = async (draft) => {
    if (!window.confirm(
      `Usunąć tankowanie ${draft.employee_name} z dnia ${draft.data} (${fmtPLN(draft.kwota)} zł)?` +
      (draft.applied ? "\n\n⚠️ Ten wpis był już zastosowany do wypłaty — usunięcie nie cofnie odliczenia z istniejącej wypłaty!" : "")
    )) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/employee-paliwo/${draft.id}`);
      await fetchDrafts();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await axios.put(`${API}/employee-paliwo/${editing.id}`, {
        data: editing.data,
        kwota: parseFloat(editing.kwota) || 0,
        target_year: parseInt(editing.target_year, 10),
        target_month: parseInt(editing.target_month, 10),
        target_period: editing.target_period,
        note: editing.note || null,
      });
      setEditing(null);
      await fetchDrafts();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const resetFilters = () => {
    setFilterEmployee("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterStatus("all");
  };

  return (
    <div className="space-y-6" data-testid="admin-tankowania-pracownikow-tab">
      {/* Sub-tab switcher */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        <button
          onClick={() => setView("list")}
          className={`px-4 py-2 -mb-px font-semibold text-sm whitespace-nowrap border-b-2 transition ${view === "list" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="paliwo-subtab-list"
        >
          Wpisy pracowników
        </button>
        <button
          onClick={() => setView("reconcile")}
          className={`px-4 py-2 -mb-px font-semibold text-sm whitespace-nowrap border-b-2 transition flex items-center gap-1.5 ${view === "reconcile" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="paliwo-subtab-reconcile"
        >
          Porównanie z Orlen
        </button>
      </div>

      {view === "reconcile" ? (
        <PaliwoReconcileView />
      ) : (
      <>
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <FunnelSimple className="w-5 h-5 text-amber-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Filtry</h3>
          <button
            onClick={resetFilters}
            className="ml-auto text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md flex items-center gap-1.5 text-slate-700"
            data-testid="reset-filters-btn"
          >
            <ArrowsCounterClockwise className="w-3.5 h-3.5" />
            Wyczyść filtry
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Pracownik
            </label>
            <select
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="input-field w-full"
              data-testid="filter-employee"
            >
              <option value="">Wszyscy pracownicy</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data od
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="input-field w-full"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data do
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="input-field w-full"
              data-testid="filter-date-to"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field w-full"
              data-testid="filter-status"
            >
              <option value="all">Wszystkie</option>
              <option value="pending">Tylko oczekujące</option>
              <option value="applied">Tylko zastosowane</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Liczba wpisów
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1 tabular-nums" data-testid="total-count">
            {totals.count}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Suma kwot
          </div>
          <div className="text-2xl font-bold text-amber-600 mt-1 tabular-nums" data-testid="total-sum">
            {fmtPLN(totals.sum)} zł
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Zastosowane
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums" data-testid="total-applied">
            {totals.applied}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Oczekujące
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="total-pending">
            {totals.pending}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GasPump className="w-5 h-5 text-amber-600" weight="duotone" />
            <h3 className="font-semibold text-slate-800">
              Tankowania pracowników
            </h3>
          </div>
          <span className="text-xs text-slate-500">
            Auto-odświeżanie co 30s
          </span>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400">Ładowanie...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            Brak wpisów dla wybranych filtrów.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Pracownik</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Data tankowania</th>
                  <th className="text-right py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Kwota</th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Opłacone</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Wypłata docelowa</th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Dodano</th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider w-28">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`admin-paliwo-row-${d.id}`}
                  >
                    <td className="py-3 px-4 font-semibold text-slate-800">
                      {d.employee_name || "—"}
                    </td>
                    <td className="py-3 px-4 text-slate-700">{d.data}</td>
                    <td className="py-3 px-4 text-right tabular-nums font-bold text-slate-800">
                      {fmtPLN(d.kwota)} zł
                    </td>
                    <td className="py-3 px-4 text-center">
                      {d.applied ? (
                        <CheckCircle
                          className="w-5 h-5 text-emerald-600 inline-block"
                          weight="fill"
                        />
                      ) : (
                        <span className="text-slate-300 text-xl">○</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      <div>
                        {MONTHS_PL[(d.target_month || 1) - 1]} {d.target_year} — {d.target_period}
                      </div>
                      {payoutDateFromDraft(d) && (
                        <div
                          className="text-[10px] text-slate-500 mt-0.5"
                          title="Faktyczna wypłata (2 tygodnie po zakończeniu okresu)"
                          data-testid={`payout-date-${d.id}`}
                        >
                          wypłata: <span className="font-semibold text-slate-700">{payoutDateFromDraft(d)}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {d.applied ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                          Zastosowano
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                          <Clock className="w-3.5 h-3.5" weight="fill" />
                          Oczekuje
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">
                      {d.created_at
                        ? new Date(d.created_at).toLocaleString("pl-PL", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setEditing({ ...d })}
                        disabled={busy}
                        className="p-1.5 hover:bg-slate-100 rounded text-slate-700 mr-1"
                        title="Edytuj"
                        data-testid={`admin-paliwo-edit-${d.id}`}
                      >
                        <PencilSimple className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(d)}
                        disabled={busy}
                        className="p-1.5 hover:bg-rose-100 rounded text-rose-600"
                        title="Usuń"
                        data-testid={`admin-paliwo-delete-${d.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              testId="tankowania-pracownikow-pagination"
            />
          </div>
        )}
      </div>
      </>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-border max-w-md w-full">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <GasPump className="w-5 h-5 text-amber-600" weight="duotone" />
                Edytuj tankowanie
              </h3>
              <button
                onClick={() => setEditing(null)}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="admin-paliwo-edit-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Pracownik</div>
                <div className="text-sm font-bold text-slate-800">{editing.employee_name || editing.employee_id}</div>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Data tankowania</label>
                <input
                  type="date"
                  value={editing.data || ""}
                  onChange={(e) => setEditing({ ...editing, data: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded"
                  data-testid="admin-paliwo-edit-data"
                />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Kwota (zł)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.kwota || ""}
                  onChange={(e) => setEditing({ ...editing, kwota: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded font-mono"
                  data-testid="admin-paliwo-edit-kwota"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Rok wypłaty</label>
                  <input
                    type="number"
                    value={editing.target_year || ""}
                    onChange={(e) => setEditing({ ...editing, target_year: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-paliwo-edit-year"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Mies.</label>
                  <select
                    value={editing.target_month || 1}
                    onChange={(e) => setEditing({ ...editing, target_month: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-paliwo-edit-month"
                  >
                    {MONTHS_PL.map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Okres</label>
                  <select
                    value={editing.target_period || "01-15"}
                    onChange={(e) => setEditing({ ...editing, target_period: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded"
                    data-testid="admin-paliwo-edit-period"
                  >
                    <option value="01-15">01-15</option>
                    <option value="16-30">16-30</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-semibold mb-1 block">Notatka (opcjonalnie)</label>
                <input
                  type="text"
                  value={editing.note || ""}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded"
                  data-testid="admin-paliwo-edit-note"
                />
              </div>
              {editing.applied && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠️ Ten wpis już został zastosowany do wypłaty. Edycja zmieni szczegóły wpisu, ale NIE zaktualizuje już obliczonej wypłaty.
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={() => setEditing(null)}
                  disabled={busy}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded text-sm font-semibold"
                  data-testid="admin-paliwo-edit-cancel"
                >
                  Anuluj
                </button>
                <button
                  onClick={saveEdit}
                  disabled={busy}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-semibold disabled:opacity-50"
                  data-testid="admin-paliwo-edit-save"
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
